import { MONTH_ABBR_PT } from '../constants/maintenance.constants';
import { formatCurrency } from './productCosts';
import type { MaintenanceSectorFertilization } from '../types';
import type {
	SimpleDataTableColumn,
	SimpleDataTableRow,
} from '../components/Tables/SimpleDataTable/SimpleDataTable';

export interface PlanningTableData {
	columns: SimpleDataTableColumn[];
	rows: SimpleDataTableRow[];
}

type ProposalItem = {
	id: string;
	dose: number;
	name?: string;
	totalDoseKg?: number;
	pricePerKg?: number;
	monthlyCost?: number;
};
type FertilizerItem = { id: string; dose: number; name?: string };

type SectorRecommendation = {
	period?: string;
	proposal?: ProposalItem[];
	balance?: number[];
	fertilizers?: FertilizerItem[];
	totalCost?: number;
};

export type PlanningSectorResult = {
	sectorId: string;
	sectorName: string;
	size: number;
	recommendations?: SectorRecommendation[] | SectorRecommendation;
	recommendation?: {
		fertilizers?: FertilizerItem[];
		demand?: Record<string, number>;
	};
};

const doseFormatter = new Intl.NumberFormat('pt-BR', {
	minimumFractionDigits: 1,
	maximumFractionDigits: 1,
});

const formatDose = (value: number | null | undefined) => {
	if (value == null || !Number.isFinite(value) || value === 0) return '—';
	return doseFormatter.format(value);
};

export const toApiPeriod = (value: string) => value.replace(/-/g, '/').slice(0, 7);

export const fromApiPeriod = (value: string) => value.replace(/\//g, '-').slice(0, 7);

export const formatPlanningMonth = (period: string) => {
	const normalized = fromApiPeriod(period);
	const [year, month] = normalized.split('-');
	const abbr = MONTH_ABBR_PT[Number(month) - 1];
	return abbr && year ? `${abbr}/${year}` : period;
};

const asRecommendationList = (sector: PlanningSectorResult): SectorRecommendation[] => {
	if (Array.isArray(sector.recommendations)) return sector.recommendations;
	if (sector.recommendations) return [sector.recommendations];
	if (sector.recommendation) return [sector.recommendation];
	return [];
};

const fertilizerName = (
	id: string,
	fallback?: string,
	products?: Array<{ _id: string; name?: string; commercialName?: string }>
) => {
	const product = products?.find((item) => item._id === id);
	return fallback || product?.name || product?.commercialName || 'Produto';
};

const collectFertilizers = (
	sectors: PlanningSectorResult[],
	products?: Array<{ _id: string; name?: string; commercialName?: string }>
) => {
	const map = new Map<string, string>();
	sectors.forEach((sector) => {
		asRecommendationList(sector).forEach((recommendation) => {
			(recommendation.proposal ?? recommendation.fertilizers ?? []).forEach((item) => {
				const id = String(item.id ?? '');
				if (!id || map.has(id)) return;
				map.set(id, fertilizerName(id, item.name, products));
			});
		});
	});
	return [...map.entries()].map(([id, name]) => ({ id, name }));
};

const periodRows = (sectors: PlanningSectorResult[], byHectare: boolean) => {
	const rows = new Map<string, Record<string, number>>();
	const source = byHectare ? sectors.slice(0, 1) : sectors;
	source.forEach((sector) => {
		const size = byHectare ? 1 : Number(sector.size) || 1;
		asRecommendationList(sector).forEach((recommendation) => {
			const period = recommendation.period || 'total';
			if (!rows.has(period)) rows.set(period, {});
			const cells = rows.get(period);
			if (!cells) return;
			(recommendation.proposal ?? recommendation.fertilizers ?? []).forEach((item) => {
				const id = String(item.id ?? '');
				if (!id) return;
				cells[id] = (cells[id] ?? 0) + Number(item.dose || 0) * size;
			});
		});
	});
	return [...rows.entries()].sort(([a], [b]) => a.localeCompare(b));
};

export const mapPlanningFertilizerDemand = (
	sectors: PlanningSectorResult[] = [],
	byHectare: boolean,
	products?: Array<{ _id: string; name?: string; commercialName?: string }>
): PlanningTableData => {
	const fertilizers = collectFertilizers(sectors, products);
	const columns: SimpleDataTableColumn[] = [
		{ key: 'month', label: 'MÊS' },
		...fertilizers.map((fertilizer) => ({
			key: fertilizer.id,
			label: fertilizer.name.toUpperCase(),
		})),
	];
	const periodEntries = periodRows(sectors, byHectare);
	const totals: Record<string, number> = {};
	const rows: SimpleDataTableRow[] = periodEntries.map(([period, cells]) => {
		fertilizers.forEach(({ id }) => {
			totals[id] = (totals[id] ?? 0) + (cells[id] ?? 0);
		});
		return {
			id: period,
			cells: {
				month: formatPlanningMonth(period),
				...Object.fromEntries(fertilizers.map(({ id }) => [id, formatDose(cells[id])])),
			},
		};
	});

	if (rows.length > 0) {
		rows.push({
			id: 'period-total',
			isTotal: true,
			cells: {
				month: 'Total do período',
				...Object.fromEntries(fertilizers.map(({ id }) => [id, formatDose(totals[id])])),
			},
		});
	}

	return { columns, rows };
};

const fertilizerTotals = (sectors: PlanningSectorResult[], byHectare: boolean) => {
	const totals: Record<string, number> = {};
	periodRows(sectors, byHectare).forEach(([, cells]) => {
		Object.entries(cells).forEach(([id, value]) => {
			totals[id] = (totals[id] ?? 0) + value;
		});
	});
	return totals;
};

const formatAdjustment = (value: number) => `${value > 0 ? '+' : ''}${value}%`;

export const mapPlanningAdjustedDemand = (
	original: PlanningSectorResult[],
	adjusted: PlanningSectorResult[],
	byHectare: boolean,
	products?: Array<{ _id: string; name?: string; commercialName?: string }>
): PlanningTableData => {
	const table = mapPlanningFertilizerDemand(adjusted, byHectare, products);
	if (!table.rows.length) return table;

	const before = fertilizerTotals(original, byHectare);
	const after = fertilizerTotals(adjusted, byHectare);
	const fertilizerIds = table.columns.slice(1).map((column) => column.key);
	const variations = Object.fromEntries(
		fertilizerIds.map((id) => {
			const base = before[id] ?? 0;
			return [id, base > 0 ? Math.round((((after[id] ?? 0) - base) / base) * 100) : 0];
		})
	);

	table.rows.push({
		id: 'adjustment',
		isTotal: true,
		cells: {
			month: 'Ajuste',
			...Object.fromEntries(fertilizerIds.map((id) => [id, formatAdjustment(variations[id])])),
		},
		cellTones: Object.fromEntries(
			fertilizerIds
				.filter((id) => variations[id] !== 0)
				.map((id) => [id, variations[id] > 0 ? 'positive' : 'negative'])
		),
	});

	return table;
};

export const mapSectorFertilizationsToPlanning = (
	items: MaintenanceSectorFertilization[] = []
): PlanningSectorResult[] =>
	items.map((item) => ({
		sectorId: item.sectorId,
		sectorName: item.sectorName,
		size: item.size,
		recommendations: Array.isArray(item.recommendations)
			? item.recommendations
			: item.recommendations
				? [item.recommendations]
				: [],
	}));

export interface PlanningBudgetMonth {
	period: string;
	label: string;
	total: number;
}

const quantityFormatter = new Intl.NumberFormat('pt-BR', {
	minimumFractionDigits: 1,
	maximumFractionDigits: 1,
});

export const formatBudgetMonth = (period: string) => {
	const [year, month] = fromApiPeriod(period).split('-');
	const abbr = MONTH_ABBR_PT[Number(month) - 1];
	return abbr && year ? `${abbr}/${year.slice(2)}` : period;
};

const budgetRecommendations = (sectors: PlanningSectorResult[], period?: string) =>
	sectors.flatMap((sector) =>
		asRecommendationList(sector).filter(
			(recommendation) => !period || recommendation.period === period
		)
	);

export const hasPlanningBudget = (sectors: PlanningSectorResult[] = []) =>
	budgetRecommendations(sectors).some((recommendation) =>
		(recommendation.proposal ?? []).some((item) => item.pricePerKg != null)
	);

export const mapPlanningBudgetMonths = (
	sectors: PlanningSectorResult[] = []
): PlanningBudgetMonth[] => {
	const totals = new Map<string, number>();
	budgetRecommendations(sectors).forEach((recommendation) => {
		if (!recommendation.period) return;
		totals.set(
			recommendation.period,
			(totals.get(recommendation.period) ?? 0) + Number(recommendation.totalCost ?? 0)
		);
	});
	return [...totals.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([period, total]) => ({ period, label: formatBudgetMonth(period), total }));
};

export const mapPlanningBudgetTable = (
	sectors: PlanningSectorResult[] = [],
	period: string,
	products?: Array<{ _id: string; name?: string; commercialName?: string }>
): PlanningTableData => {
	const columns: SimpleDataTableColumn[] = [
		{ key: 'fertilizer', label: 'FERTILIZANTE' },
		{ key: 'unitPrice', label: 'CUSTO UNITÁRIO' },
		{ key: 'quantity', label: 'QUANTIDADE' },
		{ key: 'cost', label: 'CUSTO' },
	];
	const items = new Map<
		string,
		{ name: string; pricePerKg?: number; quantity: number; cost: number; hasCost: boolean }
	>();

	budgetRecommendations(sectors, period).forEach((recommendation) => {
		(recommendation.proposal ?? []).forEach((item) => {
			const id = String(item.id ?? '');
			if (!id) return;
			const current = items.get(id) ?? {
				name: fertilizerName(id, item.name, products),
				pricePerKg: item.pricePerKg,
				quantity: 0,
				cost: 0,
				hasCost: false,
			};
			current.quantity += Number(item.totalDoseKg ?? 0);
			current.cost += Number(item.monthlyCost ?? 0);
			current.hasCost ||= item.monthlyCost != null;
			items.set(id, current);
		});
	});

	const entries = [...items.entries()];
	const totalQuantity = entries.reduce((sum, [, item]) => sum + item.quantity, 0);
	const totalCost = entries.reduce((sum, [, item]) => sum + item.cost, 0);
	const rows: SimpleDataTableRow[] = entries.map(([id, item]) => ({
		id,
		cells: {
			fertilizer: item.name,
			unitPrice: item.pricePerKg != null ? `${formatCurrency(item.pricePerKg)}/kg` : '—',
			quantity: `${quantityFormatter.format(item.quantity)} kg`,
			cost: item.hasCost ? formatCurrency(item.cost) : '—',
		},
	}));

	if (rows.length > 0) {
		rows.push({
			id: 'budget-total',
			isTotal: true,
			cells: {
				fertilizer: 'Total do período',
				unitPrice: '',
				quantity: `${quantityFormatter.format(totalQuantity)} kg`,
				cost: formatCurrency(totalCost),
			},
		});
	}

	return { columns, rows };
};
